/* Theme custom file */
/**
 * Quick add alternate
 *
 * Handles the quick-add flow for product-card-alternate:
 * button click -> fetch section -> create modal -> show -> cleanup on close.
 *
 * BRD-510: the modal reuses the native DialogComponent (@theme/dialog) so it
 * gets the native open/close animation, body scroll-lock, outside-click and
 * Escape handling, and closes on cart update -- matching the native quick-add
 * dialog and the PDP add-to-cart flow. The wrapper element carries the native
 * `quick-add-modal` class so it inherits the native fly-to-cart suppression
 * (a fly-to-cart launched from inside a modal would only animate hidden behind
 * the backdrop). The class sits on the wrapper, not the <dialog>, so the native
 * `.quick-add-modal` sizing/animation rules don't fight our custom dialog; the
 * wrapper is `display: contents` (see the section CSS) so those rules paint
 * nothing. The visible add-to-cart feedback here is the modal closing into the
 * cart drawer slide-in, like the native quick-add.
 *
 * Also drives the size row the card shows on hover. A size click either adds
 * the variant straight to the cart (single-option product) or opens this same
 * modal on that size (multi-option product, "…" overflow button).
 */

import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { isSaleClosed } from '@theme/sale-gate';

const SECTION_ID = 'quick-add-alternate';
const ERROR_MESSAGE_DISPLAY_DURATION = 4000;

/**
 * Quick-add dialog. Extends the native DialogComponent and closes itself when
 * the product is added to cart (cart:update), mirroring the native QuickAddDialog.
 */
class QuickAddAltDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /** @param {CustomEvent} event */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    // On add-to-cart the cart drawer opens on this same tick and takes over the
    // body scroll-lock. The animated closeDialog() restores the body only after
    // its ~0.2s animation, which would land after the drawer has locked and
    // unlock the background behind it. Close synchronously here so the drawer
    // locks from the correct scroll position; the animated close is kept for
    // manual dismissals (close button, backdrop, Escape).
    this.#closeForCartDrawerHandoff();
  };

  /**
   * Closes the dialog immediately and restores the body scroll state
   * synchronously (mirrors DialogComponent.closeDialog's restore step) so the
   * cart drawer opening on the same tick can re-lock from the right position.
   */
  #closeForCartDrawerHandoff() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    const top = document.body.style.top;
    const scrollY = top ? -parseInt(top, 10) : 0;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: scrollY, behavior: 'instant' });

    dialog.close();
    this.dispatchEvent(new DialogCloseEvent());
  }
}

if (!customElements.get('quick-add-alt-dialog')) {
  customElements.define('quick-add-alt-dialog', QuickAddAltDialog);
}

/**
 * Fetches the quick-add section HTML for a given product URL.
 * @param {string} productUrl - The product URL (e.g. /products/some-handle)
 * @param {string} [variantId] - Variant to pre-select in the rendered picker and form
 * @returns {Promise<string>} The section HTML
 */
async function fetchQuickAddSection(productUrl, variantId) {
  const url = new URL(productUrl, window.location.origin);
  url.searchParams.set('section_id', SECTION_ID);
  if (variantId) url.searchParams.set('variant', variantId);

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch quick add section: ${response.status}`);
  }

  return response.text();
}

/**
 * Makes the modal's native variant picker refresh from this section instead of
 * the full product page. Its SECTION_ID_MAP doesn't know this modal, so it would
 * fetch the PDP, where the modal's price block doesn't exist, leaving the price
 * stuck on the opening variant. Patched here rather than in variant-picker.js to
 * keep that Horizon file on auto-updates.
 * @param {HTMLElement} dialogComponent
 */
function scopeVariantPickerToSection(dialogComponent) {
  const picker = /** @type {import('@theme/variant-picker').default | null} */ (
    dialogComponent.querySelector('variant-picker')
  );
  if (!picker) return;

  const buildRequestUrl = picker.buildRequestUrl.bind(picker);
  picker.buildRequestUrl = (...args) => {
    const url = new URL(buildRequestUrl(...args), window.location.origin);
    url.searchParams.set('section_id', SECTION_ID);
    return url.pathname + url.search;
  };

  // Rendered on the product URL, the picker believes it is on the PDP and would
  // push ?variant= into the collection URL.
  delete picker.dataset.templateProductMatch;
}

/**
 * Holds an add-to-cart submitted while the picker is still fetching the size
 * just clicked, and re-issues it once the form knows that size.
 *
 * On a size click, product-form.js flags a variant change in progress until the
 * picker's variant:update lands. A submit in between is queued with the variant
 * id it can read at that moment: the page URL's ?variant first (the PDP's own
 * variant when this modal opens over a product page), else the form's hidden id
 * input, which still holds the previous size. Both are stale here, since this
 * modal never writes ?variant (see scopeVariantPickerToSection), so the add
 * went through with the previous size. The product form resets its flag and
 * the input on variant:update before this listener runs (it listens closer to
 * the picker), so the replayed submit takes the regular path on the up-to-date
 * form. Patched on the instance, like buildRequestUrl above, to keep
 * product-form.js on auto-updates.
 * @param {QuickAddAltDialog} dialogComponent
 */
function deferSubmitUntilVariantUpdate(dialogComponent) {
  const productForm = /** @type {(HTMLElement & { handleSubmit: (event: Event) => void }) | null} */ (
    dialogComponent.querySelector('product-form-component')
  );
  if (!productForm || typeof productForm.handleSubmit !== 'function') return;

  const handleSubmit = productForm.handleSubmit.bind(productForm);
  let variantChangeInProgress = false;
  /** @type {Event | undefined} */
  let heldSubmit;

  productForm.handleSubmit = (event) => {
    if (!variantChangeInProgress) return handleSubmit(event);
    event.preventDefault();
    heldSubmit = event;
  };

  // A dismissal drops the held add-to-cart. closeDialog keeps the dialog open
  // and connected while its close animation runs, so the replay below cannot
  // tell a closing modal from an open one on its own.
  const closeDialog = dialogComponent.closeDialog;
  dialogComponent.closeDialog = () => {
    heldSubmit = undefined;
    return closeDialog();
  };

  dialogComponent.addEventListener(ThemeEvents.variantSelected, () => {
    variantChangeInProgress = true;
  });

  dialogComponent.addEventListener(ThemeEvents.variantUpdate, () => {
    variantChangeInProgress = false;
    const event = heldSubmit;
    heldSubmit = undefined;
    // The cart:update handoff removes the modal synchronously without going
    // through closeDialog (an earlier add landing meanwhile).
    if (event && dialogComponent.isConnected) handleSubmit(event);
  });
}

/**
 * Creates the quick-add dialog component and appends it to the body.
 * @param {string} html - The section HTML to inject
 * @param {string} [closeLabel]
 * @param {{ hideVendor?: boolean }} [options]
 * @returns {QuickAddAltDialog} The dialog component element
 */
function createModal(html, closeLabel, { hideVendor = false } = {}) {
  const dialogComponent = /** @type {QuickAddAltDialog} */ (document.createElement('quick-add-alt-dialog'));
  // Native class on the wrapper (not the dialog) to inherit the fly-to-cart
  // suppression without inheriting the native modal's sizing/animation rules.
  dialogComponent.classList.add('quick-add-modal');

  dialogComponent.innerHTML = `
    <dialog class="quick-add-alt-modal" ref="dialog">
      <button class="quick-add-alt-modal__close">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.5 3.5L3.5 12.5M3.5 3.5L12.5 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
      <div class="quick-add-alt-modal__content">${html}</div>
    </dialog>
  `;

  const vendor = dialogComponent.querySelector('.quick-add-alt__vendor');
  if (hideVendor && vendor) {
    // Keep the line's height so the layout doesn't shift, without the brand name.
    vendor.textContent = '\u00a0';
    vendor.setAttribute('aria-hidden', 'true');
  }

  document.body.appendChild(dialogComponent);
  scopeVariantPickerToSection(dialogComponent);
  deferSubmitUntilVariantUpdate(dialogComponent);

  const closeBtn = dialogComponent.querySelector('.quick-add-alt-modal__close');
  closeBtn?.setAttribute('aria-label', closeLabel || 'Fermer');
  closeBtn?.addEventListener('click', () => dialogComponent.closeDialog());

  // Remove the component from the DOM once its close animation has finished.
  dialogComponent.addEventListener(DialogCloseEvent.eventName, () => dialogComponent.remove(), { once: true });

  return dialogComponent;
}

/**
 * Opens the quick-add modal for a product, optionally on a given variant.
 * @param {string} productUrl
 * @param {string} [closeLabel]
 * @param {string} [variantId]
 * @param {Element} [trigger] - The clicked element, inside the originating card
 */
async function openQuickAdd(productUrl, closeLabel, variantId, trigger) {
  const html = await fetchQuickAddSection(productUrl, variantId);
  // The modal mirrors its card. A card without the vendor line hides the brand here too.
  const card = trigger?.closest('.product-card-alt');
  const dialogComponent = createModal(html, closeLabel, { hideVendor: !card?.hasAttribute('data-show-vendor') });
  dialogComponent.showDialog();
}

/**
 * Adds a variant to the cart through the quick-add section's own product form,
 * fetched in the background. That form carries whatever every add-to-cart
 * surface ships (the delivery-estimate line item properties), so the card never
 * has to know about it. Mirrors product-form.js: same endpoint, same `sections`
 * payload, same cart:update event, so the drawer, the cart icon and Klaviyo
 * react exactly as they do to a form submit.
 * @param {HTMLElement} size - The size clicked, the cart:update event is dispatched on it
 * @param {string} productUrl
 * @param {string} variantId
 * @param {string} productId
 * @returns {Promise<{ ok: boolean, message?: string, gated?: boolean }>}
 */
async function addVariantFromSection(size, productUrl, variantId, productId) {
  const html = await fetchQuickAddSection(productUrl, variantId);
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const saleGate = doc.querySelector('sale-gate');
  if (saleGate instanceof HTMLElement && (saleGate.hasAttribute('closed') || isSaleClosed(saleGate))) {
    return { ok: false, message: saleGate.dataset.closedLabel, gated: true };
  }

  const form = doc.querySelector('form[action$="/cart/add"]');
  if (!(form instanceof HTMLFormElement)) throw new Error('Quick add section has no product form');

  const formData = new FormData(form);
  formData.set('id', variantId);
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'), (el) =>
    el instanceof HTMLElement ? el.dataset.sectionId : ''
  ).filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const cfg = fetchConfig('javascript', { body: formData });
  const response = await fetch(Theme.routes.cart_add_url, {
    ...cfg,
    headers: { ...cfg.headers, Accept: 'text/html' },
  }).then((r) => r.json());

  if (response.status) {
    document.dispatchEvent(new CartErrorEvent(productId, response.message, response.description, response.errors));
    return { ok: false, message: response.message };
  }

  // Same event a product form dispatches after a successful add, source and
  // variant sourceId included: the add did go through the section's product
  // form, and the cart drawer (cart-drawer-alternate, opens on that source) and
  // the cart icon (adds itemCount to its bubble for that source) key their
  // behaviour off it. Dispatched on the size, which bubbles to the document, so
  // tb-analytics.js attributes the add to the card it came from. A filter or
  // sort change during the add can replace the grid: the size is then out of
  // the page and the event goes to the document, unattributed but still seen.
  (size.isConnected ? size : document).dispatchEvent(
    new CartAddEvent({}, variantId, {
      source: 'product-form-component',
      itemCount: Number(formData.get('quantity')) || 1,
      productId,
      variantId,
      sections: response.sections,
    })
  );
  return { ok: true };
}

/**
 * Keeps the size row on one line. Hides the sizes that overflow the
 * card behind the "…" button, which opens the quick add with every size.
 * Measured on each hover (a handful of buttons) so it follows the grid width.
 * @param {HTMLElement} container
 */
function fitSizes(container) {
  const more = container.querySelector('.product-card-alt__size--more');
  const sizes = /** @type {HTMLElement[]} */ (
    Array.from(container.querySelectorAll('.product-card-alt__size:not(.product-card-alt__size--more)'))
  );
  if (!(more instanceof HTMLElement) || sizes.length === 0) return;

  // The row is centred, so an overflowing row spills on both sides and offsets
  // go negative. Measure left-aligned from zero, then hand the centring back.
  container.style.justifyContent = 'flex-start';
  for (const size of sizes) size.hidden = false;
  more.hidden = true;

  const width = container.clientWidth;
  const last = sizes[sizes.length - 1];
  if (last.offsetLeft + last.offsetWidth > width) {
    more.hidden = false;
    const gap = parseFloat(getComputedStyle(container).columnGap) || 0;
    const limit = width - more.offsetWidth - gap;
    let overflowing = false;
    for (const size of sizes) {
      if (!overflowing && size.offsetLeft + size.offsetWidth > limit) overflowing = true;
      size.hidden = overflowing;
    }
  }

  container.style.justifyContent = '';
}

/**
 * Shows the cart's refusal message over the size row for a moment.
 * @param {HTMLElement} container
 * @param {string} message
 */
function showSizeError(container, message) {
  let error = /** @type {HTMLElement | null} */ (container.querySelector('.product-card-alt__sizes-error'));
  if (!error) {
    error = document.createElement('span');
    error.className = 'product-card-alt__sizes-error';
    error.setAttribute('role', 'alert');
    container.appendChild(error);
  }
  error.textContent = message;
  error.hidden = false;
  clearTimeout(Number(error.dataset.timeout));
  error.dataset.timeout = String(
    setTimeout(() => {
      error.hidden = true;
    }, ERROR_MESSAGE_DISPLAY_DURATION)
  );
}

// Event delegation — single listener for all quick-add buttons
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.product-card-alt__quick-add');
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  const productUrl = btn.dataset.productUrl;
  if (!productUrl) return;

  btn.classList.add('is-loading');
  btn.disabled = true;

  try {
    await openQuickAdd(productUrl, btn.dataset.closeLabel, undefined, btn);
  } catch (error) {
    console.error('[quick-add-alternate]', error);
  } finally {
    btn.classList.remove('is-loading');
    btn.disabled = false;
  }
});

/**
 * Fits the size row when the pointer enters a card, or when keyboard focus
 * reveals it (:focus-within).
 * @param {PointerEvent | FocusEvent} e
 */
function fitCardSizes(e) {
  if (!(e.target instanceof Element)) return;
  const card = e.target.closest('.product-card-alt[data-size-hover]');
  if (!card || (e.relatedTarget instanceof Node && card.contains(e.relatedTarget))) return;
  const sizes = card.querySelector('.product-card-alt__sizes');
  if (sizes instanceof HTMLElement) fitSizes(sizes);
}
document.addEventListener('pointerover', fitCardSizes);
document.addEventListener('focusin', fitCardSizes);

// Size click. Single-option product: add straight to cart. Otherwise,
// and for "…": open the quick add, on that size when there is one.
document.addEventListener('click', async (e) => {
  const size = e.target.closest('.product-card-alt__size');
  if (!size || size.disabled || size.classList.contains('is-loading')) return;

  e.preventDefault();
  e.stopPropagation();

  const container = size.closest('.product-card-alt__sizes');
  if (!container) return;

  const { productUrl, productId, closeLabel, sizeMode } = container.dataset;
  const isMore = size.classList.contains('product-card-alt__size--more');
  const variantId = isMore ? undefined : size.dataset.variantId;
  if (!productUrl || (!isMore && !variantId)) return;

  size.classList.add('is-loading');

  try {
    if (isMore || sizeMode === 'modal') {
      await openQuickAdd(productUrl, closeLabel, variantId, size);
      return;
    }

    const result = await addVariantFromSection(size, productUrl, variantId, productId);
    if (!result.ok) {
      // Sold out under the buyer's cursor: grey the size right on the card and
      // surface the cart's own message. A closed sale is not a sold-out size.
      if (!result.gated) size.disabled = true;
      showSizeError(container, result.message);
    }
  } catch (error) {
    console.error('[quick-add-alternate]', error);
    // Server or network failure on a direct add: the buyer still needs to see
    // that nothing went into the cart.
    if (!isMore && sizeMode !== 'modal' && container.dataset.errorLabel) {
      showSizeError(container, container.dataset.errorLabel);
    }
  } finally {
    size.classList.remove('is-loading');
  }
});
